import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, addDoc, query, where, getDocs, Timestamp, doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage'; // Add deleteObject and ref
import { db, storage } from '../../firebase'; // Add storage
import { v4 as uuidv4 } from 'uuid';
import ErrorBanner from '../ErrorBanner';
import Modal from '../Modal';
import ListForm from '../PackingLists/ListForm'; // Import the ListForm component
import './MenuPackingLists.css';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import FormattedDate from '../FormattedDate';
import { Row, Col, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import BadgeGroup from '../BadgeGroup';

/**
 * PackingLists component that displays the user's packing lists and allows creating new ones
 * 
 * @param {Object} props - Component props
 * @param {Object} props.user - Current authenticated user object
 * @returns {JSX.Element} Rendered component
 */
const PackingLists = ({ user }) => {
  const [lists, setLists] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isAddListModalOpen, setIsAddListModalOpen] = useState(false); // New state for add list modal
  const [newList, setNewList] = useState({ // New state for the new list
    name: '',
    tripType: '',
    photo: ''
  });
  const navigate = useNavigate();

  /**
   * Fetch user's lists from Firestore
   */
  useEffect(() => {
    /**
     * Asynchronously fetch lists belonging to the current user
     */
    const fetchLists = async () => {
      if (!user) return;
      setLoading(true); // Set loading to true when starting fetch
      try {
        const q = query(collection(db, "lists"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const listsData = [];
        querySnapshot.forEach((doc) => {
          listsData.push({ id: doc.id, ...doc.data() });
        });
        setLists(listsData);
      } catch (err) {
        console.error("Error fetching lists:", err);
        setError("Failed to load your lists. Please refresh the page to try again.");
      } finally {
        setLoading(false); // Set loading to false when done
      }
    };
    fetchLists();
  }, [user]);

  /**
   * Transform Firebase Storage URL to use the thumb version
   * 
   * @param {string} url - Original Firebase Storage URL (may be medium or original)
   * @returns {string} URL with _resized_thumb added before the file extension
   */
  const getThumbImageUrl = (url) => {
    if (!url) return url;
    
    // Check if URL is a Firebase Storage URL
    if (!url.includes('firebasestorage.googleapis.com')) return url;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return url;
    
    const encodedFilename = filenameMatch[1]; // This is URL encoded
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any existing resize suffix (in case it's already a medium version)
    const baseFilename = decodedFilename.replace(/_resized_(medium|thumb)/g, '');
    
    // Extract file extension and directory
    const lastDotIndex = baseFilename.lastIndexOf('.');
    if (lastDotIndex === -1) return url; // No extension found
    
    const lastSlashIndex = baseFilename.lastIndexOf('/');
    const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
    const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
    
    const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Create new filename with _resized_thumb suffix
    const newFilename = `${directory}${filenameWithoutExt}_resized_thumb${extension}`;
    const encodedNewFilename = encodeURIComponent(newFilename);
    
    // Replace the filename in the URL
    return url.replace(encodedFilename, encodedNewFilename);
  };
  
  /**
   * Extract the base filename from a Firebase Storage URL
   * 
   * @param {string} url - Firebase Storage URL
   * @returns {string|null} Base filename without resize suffix or null if can't parse
   */
  const getBaseFilenameFromUrl = (url) => {
    if (!url) return null;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return null;
    
    const encodedFilename = filenameMatch[1];
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any resize suffix from the filename
    return decodedFilename.replace(/_resized_(medium|thumb)/g, '');
  };
  
  /**
   * Delete image files from storage
   * 
   * @param {string} url - URL of the image to delete
   * @returns {Promise<void>}
   */
  const deleteImages = async (url) => {
    if (!url) return;
    try {
      const baseFilename = getBaseFilenameFromUrl(url);
      if (!baseFilename) {
        console.log('Error: Could not extract base filename from URL:', url);
        return;
      }
      console.log('Attempting to delete images with base filename:', baseFilename);
      const fileDir = baseFilename.includes('/') ? baseFilename.substring(0, baseFilename.lastIndexOf('/')) : '';
      const fileName = baseFilename.includes('/') ? baseFilename.substring(baseFilename.lastIndexOf('/') + 1) : baseFilename;
      const fileExt = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      
      // Create references to both resized versions
      const mediumRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_medium${fileExt}`);
      const thumbRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_thumb${fileExt}`);
      
      // Delete both files
      await Promise.all([
        deleteObject(mediumRef).catch(err => console.log('Medium file may not exist:', err)),
        deleteObject(thumbRef).catch(err => console.log('Thumb file may not exist:', err))
      ]);
      console.log('Successfully deleted image files');
    } catch (error) {
      console.error('Error deleting images:', error);
      // Continue with list deletion even if image deletion fails
    }
  };

  /**
   * Create a new packing list with the form data
   * 
   * @returns {Promise<void>}
   */
  const createNewList = async (e) => {
    e.preventDefault();
    try {
      const publicId = uuidv4();
      const now = Timestamp.now();
      // Generate a unique ID for the list
      const listData = {
        name: newList.name,
        created: now,
        lastEdited: now,
        tripType: newList.tripType || '',
        photo: newList.photo || null,  // Add photo field
        items: [],
        publicId: publicId,
        userId: user.uid,
        ownerName: user.displayName,
        ownerEmail: user.email,
        ownerPhotoURL: user.photoURL || null
      };
      
      const docRef = await addDoc(collection(db, "lists"), listData);
      
      // Track the event
      logEvent(analytics, 'create_list', {
        user_id: user.uid,
        timestamp: new Date().toString()
      });
      
      // Reset form
      setNewList({
        name: '',
        tripType: '',
        photo: ''
      });
      
      setIsAddListModalOpen(false);
      setLists([...lists, { id: docRef.id, ...listData }]);
    } catch (error) {
      console.error("Error creating list:", error);
      setError("Failed to create a new list. Please try again.");
    }
  };

  /**
   * Delete a list from Firestore
   * 
   * @param {string} listId - ID of the list to delete
   * @returns {Promise<void>}
   */
  const deleteList = async (listId) => {
    try {
      // Find the list to get its photo URL before deleting
      const listToDelete = lists.find(list => list.id === listId);
      
      // Delete the document from Firestore
      await deleteDoc(doc(db, "lists", listId));
      
      // Delete associated images if they exist
      if (listToDelete && listToDelete.photo) {
        await deleteImages(listToDelete.photo);
      }
      
      // Update local state
      setLists(lists.filter(list => list.id !== listId));
    } catch (error) {
      console.error("Error deleting list:", error);
      setError("Failed to delete the list. Please try again.");
    } finally {
      setConfirmDeleteId(null); // Close confirmation dialog
    }
  };

  /**
   * Handle clicking the delete button by showing confirmation
   * 
   * @param {Event} e - Click event
   * @param {string} listId - ID of the list to potentially delete
   */
  const handleDeleteClick = (e, listId) => {
    e.preventDefault(); // Prevent navigation to the list
    e.stopPropagation(); // Prevent event bubbling
    setConfirmDeleteId(listId);
  };

  /**
   * Cancel the delete operation
   */
  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };
  
  /**
   * Render the add list modal
   */
  const renderAddListModal = () => {
    return (
      <Modal
        isOpen={isAddListModalOpen}
        onClose={() => setIsAddListModalOpen(false)}
        title="Create New Packing List"
      >
        <div className="add-list-modal">
          <ListForm
            list={newList}
            setList={setNewList}
            onSubmit={createNewList}
            onCancel={() => setIsAddListModalOpen(false)}
            submitButtonText="Create List"
            isEdit={false}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div className="packing-lists-container">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <Button
        variant="primary"
        onClick={() => setIsAddListModalOpen(true)}
        className="btn-primary-centered">
          Add New Packing List
      </Button>
      
      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading your motorcycles...</p>
        </div>
      ) : lists.length > 0 ? (
        <div className="items-container">
          <Row className="g-4">
            {lists.map(list => (
              <Col key={list.id} xs={12} md={12} lg={12} xl={12}>
                <Card className="item-panel h-100">
                  <Link to={`/list/${list.publicId}`} className="text-decoration-none h-100 d-flex flex-column">
                    <Card.Header className="item-panel-header">
                      <h3 className="heading-small mb-0">{list.name}</h3>
                    </Card.Header>
                    
                    <Card.Body className="item-panel-content flex-grow-1 position-relative pt-3 pb-2">
                      <div className="d-flex align-items-start">
                        {list.photo && (
                          <div className="item-panel-media" style={{ marginTop: '-30px' }}>
                            <img 
                              src={getThumbImageUrl(list.photo)}
                              alt={list.name}
                            />
                          </div>
                        )}
                        
                        <div className="item-panel-body ms-2">
                          <div className="item-panel-details">
                            {list.tripType && (
                              <Badge bg="primary">{list.tripType}</Badge>
                            )}
                          </div>
                          <div className="item-panel-details">
                            {list.created && (
                              <BadgeGroup
                                label="Created"
                                value={list.created ? <FormattedDate date={list.created}/> : ''}
                                color="secondary"
                                />
                            )}

                            {list.lastEdited && (
                              <BadgeGroup
                                label="Last Edited"
                                value={list.lastEdited ? <FormattedDate date={list.lastEdited}/> : ''}
                                color="primary"
                                />
                            )}

                            {list.items && (
                              <BadgeGroup
                                label="Items"
                                value={list.items.length}
                                color="success"
                                />
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                    
                    <Card.Footer className="item-panel-footer mt-auto">
                      <Button 
                        variant="danger" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(e, list.id);
                        }}
                        size="sm"
                      >
                        Delete
                      </Button>
                    </Card.Footer>
                  </Link>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <Alert variant="info" className="text-center p-4">
          <p className="mb-0">You don't have any packing lists yet. Create your first one to get started!</p>
        </Alert>
      )}
      
      {/* Add list modal */}
      {renderAddListModal()}
      
      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmDeleteId !== null}
        onClose={cancelDelete}
        title="Confirm Delete"
      >
        <div className="delete-confirmation">
          <p>Are you sure you want to delete this list? This action cannot be undone.</p>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => deleteList(confirmDeleteId)}>
              Delete List
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PackingLists;
